import { useFetcher } from '@remix-run/react'
import { useEffect, useMemo, useState } from 'react'
import {
  ValidatedForm,
  useControlField,
  useFormContext,
} from 'remix-validated-form'
import {
  aMapCurrentCount,
  aMapFilters,
  aMapViewState,
  aUser,
  useAtomValue,
} from '~/atoms'
import {
  FieldContainer,
  HiddenFieldValues,
  HiddenFields,
  LoadingCircle,
  TextField,
} from '~/components'
import { Badge, Button, Dialog, Textarea } from '~/components/ui'
import { useFormHelpers } from '~/hooks'
import {
  SearchEmailNotificationFrequency,
  getDefaultValues,
  getMapFilterLabels,
  searchValidator,
} from '~/models'
import { UpdateNotificationsDropdown } from './Actions'
import useSearchLocation from './useSearchLocation'

function SavedSearchesForm({ onSearchCreate }: { onSearchCreate: () => void }) {
  const user = useAtomValue(aUser)
  const mapFilters = useAtomValue(aMapFilters)
  const currentMapCount = useAtomValue(aMapCurrentCount)
  const mapViewState = useAtomValue(aMapViewState)

  const formId = 'search-form'
  const fetcher = useFetcher()
  const formContext = useFormContext(formId)
  useFormHelpers(formId, formContext)
  const [description, setDescription] = useControlField<string>(
    'description',
    formId
  )
  const [notificationFrequency, setNotificationFrequency] =
    useControlField<SearchEmailNotificationFrequency>(
      'notificationFrequency',
      formId
    )

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      onSearchCreate()
    }
  }, [fetcher, onSearchCreate])

  const searchLocation = useSearchLocation()
  const isDrawnFeature = useMemo(() => {
    return (
      searchLocation.$geoWithin &&
      'properties' in searchLocation.$geoWithin.$geometry
    )
  }, [searchLocation])

  const defaultValues = useMemo(() => {
    return getDefaultValues(user, {
      name: '',
      filters: mapFilters,
      location: searchLocation,
      notificationFrequency: 'never',
    })
  }, [mapFilters, searchLocation, user])

  return (
    <ValidatedForm
      id={formId}
      action="/api/actions/create-search"
      method="post"
      fetcher={fetcher}
      validator={searchValidator}
      defaultValues={defaultValues}>
      <HiddenFields slugFields={['name']} />
      <HiddenFieldValues name="filters" value={mapFilters} />
      <HiddenFieldValues name="location" value={searchLocation} />
      <input
        type="hidden"
        name="notificationFrequency"
        value={notificationFrequency}
      />
      <input
        type="hidden"
        name="listingsCount"
        value={currentMapCount?.toString()}
      />
      <input type="hidden" name="lat" value={mapViewState?.lat?.toString()} />
      <input type="hidden" name="lng" value={mapViewState?.lng?.toString()} />
      <input type="hidden" name="z" value={mapViewState?.zoom?.toString()} />

      <div className="flex flex-col gap-6">
        <TextField
          required
          label="Name"
          name="name"
          placeholder="Name"
          disabled={fetcher.state === 'submitting'}
        />
        <FieldContainer label="Description" id="description">
          <Textarea
            id="description"
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={fetcher.state === 'submitting'}
          />
        </FieldContainer>

        <div>
          <p className="mb-2 font-medium">Search Update Notifications</p>
          <UpdateNotificationsDropdown
            notificationFrequency={notificationFrequency}
            updateFrequency={setNotificationFrequency}
            modal={true}
            size="default"
          />
        </div>

        <div className="">
          <p className="mb-2 font-medium">
            Currently Matching {currentMapCount?.toLocaleString()} Listings
          </p>
          <div className="flex flex-wrap gap-2">
            {isDrawnFeature && (
              <Badge variant="secondary" className="px-1 text-xs">
                Map Geography
              </Badge>
            )}
            {getMapFilterLabels(mapFilters).map((label) => (
              <Badge key={label} variant="secondary" className="px-1 text-xs">
                {label}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      <Dialog.Footer className="mt-4">
        <Dialog.Close asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </Dialog.Close>
        <Button
          type="submit"
          disabled={fetcher.state !== 'idle'}
          onClick={async (e) => {
            e.preventDefault()
            formContext.submit()
          }}>
          {fetcher.state !== 'idle' && (
            <LoadingCircle className="mr-2 h-5 w-5" />
          )}
          {fetcher.state !== 'idle' ? 'Saving' : 'Save'}
        </Button>
      </Dialog.Footer>
    </ValidatedForm>
  )
}

export default function SavedSearchesDialog({
  onSearchCreate,
}: {
  onSearchCreate: () => void
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
      }}
      modal={true}>
      <Dialog.Trigger asChild>
        <Button size="sm" variant="default" className="h-8">
          Create
        </Button>
      </Dialog.Trigger>
      <Dialog.Content className="sm:max-w-[425px]">
        <Dialog.Header>
          <Dialog.Title className="font-normal">
            Save Current Search
          </Dialog.Title>
          <Dialog.Description>
            Save the current map view and filters
          </Dialog.Description>
        </Dialog.Header>

        {isOpen && (
          <SavedSearchesForm
            onSearchCreate={() => {
              setIsOpen(false)
              onSearchCreate()
            }}
          />
        )}
      </Dialog.Content>
    </Dialog>
  )
}
